import { useState } from 'react';
import {
  Phone,
  PhoneIncoming,
  PhoneOutgoing,
  Play,
  Pause,
  ChevronRight,
  RefreshCw,
} from 'lucide-react';
import { clsx } from 'clsx';
import type { Call, CallFilters } from '../types';
import {
  formatDuration,
  formatDateTime,
  getDirectionLabel,
  getPhaseLabel,
} from '../utils/format';

interface CallListProps {
  calls: Call[];
  loading: boolean;
  filters: CallFilters;
  total: number;
  hasMore: boolean;
  onLoadMore: () => void;
  onCallClick: (call: Call) => void;
  onRefresh: () => void;
  compact?: boolean;
}

export default function CallList({
  calls,
  loading,
  total,
  hasMore,
  onLoadMore,
  onCallClick,
  onRefresh,
  compact = false,
}: CallListProps) {
  const [playingId, setPlayingId] = useState<string | null>(null);

  const handlePlayPause = (e: React.MouseEvent, call: Call) => {
    e.stopPropagation();
    if (playingId === call.id) {
      setPlayingId(null);
    } else {
      setPlayingId(call.id);
    }
  };

  const getLeadScoreColor = (score: number | undefined): string => {
    if (score === undefined) return 'text-gray-400';
    if (score > 25) return 'text-red-600';
    if (score >= 0) return 'text-yellow-600';
    return 'text-blue-600';
  };

  const getLeadScoreLabel = (score: number | undefined): string => {
    if (score === undefined) return '-';
    if (score > 25) return 'Heiss';
    if (score >= 0) return 'Warm';
    return 'Kalt';
  };

  if (loading && calls.length === 0) {
    return (
      <div className="card">
        <div className="p-6 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-2 text-sm text-gray-500">Lade Anrufe...</p>
        </div>
      </div>
    );
  }

  if (calls.length === 0) {
    return (
      <div className="card">
        <div className="p-8 text-center">
          <Phone className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Keine Anrufe</h3>
          <p className="mt-1 text-sm text-gray-500">
            Keine Anrufe fur die ausgewahlten Filter gefunden.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="card overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900">
            {compact ? 'Letzte Anrufe' : 'Anrufe'}
          </h3>
          <p className="text-sm text-gray-500">{total} Anrufe insgesamt</p>
        </div>
        <button
          onClick={onRefresh}
          className="btn-secondary"
          disabled={loading}
        >
          <RefreshCw
            className={clsx('w-4 h-4 mr-2', loading && 'animate-spin')}
          />
          Aktualisieren
        </button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="table">
          <thead>
            <tr>
              <th>Anruftyp</th>
              <th>Nutzer</th>
              <th>Kunde</th>
              <th>Gestartet</th>
              <th>Dauer</th>
              {!compact && <th>Tags</th>}
              <th>Audio</th>
              <th>Leadprioritat</th>
              <th>Phase</th>
              <th></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {calls.map((call) => (
              <tr
                key={call.id}
                onClick={() => onCallClick(call)}
                className="cursor-pointer"
              >
                {/* Direction */}
                <td>
                  <div className="flex items-center space-x-2">
                    {call.direction === 'inbound' ? (
                      <PhoneIncoming className="w-4 h-4 text-green-500" />
                    ) : (
                      <PhoneOutgoing className="w-4 h-4 text-blue-500" />
                    )}
                    <span className="text-sm">
                      {getDirectionLabel(call.direction)}
                    </span>
                  </div>
                </td>

                {/* Agent */}
                <td>
                  <span className="text-sm font-medium">
                    {call.agent_name || 'Unbekannt'}
                  </span>
                </td>

                {/* Customer */}
                <td>
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      {call.company_name || call.contact_name || 'Unbekannt'}
                    </div>
                    {call.phone_number && (
                      <div className="text-sm text-gray-500">
                        {call.phone_number}
                      </div>
                    )}
                  </div>
                </td>

                {/* Started */}
                <td>
                  <span className="text-sm text-gray-500">
                    {formatDateTime(call.started_at)}
                  </span>
                </td>

                {/* Duration */}
                <td>
                  <span className="text-sm">
                    {formatDuration(call.duration_seconds)}
                  </span>
                </td>

                {/* Tags */}
                {!compact && (
                  <td>
                    <div className="flex flex-wrap gap-1">
                      {call.evaluation?.tags?.slice(0, 2).map((tag, i) => (
                        <span key={i} className="badge badge-gray">
                          {tag}
                        </span>
                      ))}
                      {(call.evaluation?.tags?.length || 0) > 2 && (
                        <span className="badge badge-gray">
                          +{(call.evaluation?.tags?.length || 0) - 2}
                        </span>
                      )}
                    </div>
                  </td>
                )}

                {/* Audio */}
                <td>
                  {call.recording_url ? (
                    <button
                      onClick={(e) => handlePlayPause(e, call)}
                      className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                    >
                      {playingId === call.id ? (
                        <Pause className="w-4 h-4 text-primary-600" />
                      ) : (
                        <Play className="w-4 h-4 text-gray-600" />
                      )}
                    </button>
                  ) : (
                    <span className="text-sm text-gray-400">-</span>
                  )}
                </td>

                {/* Lead Score */}
                <td>
                  <div className="flex items-center space-x-1">
                    <span
                      className={clsx(
                        'text-sm font-semibold',
                        getLeadScoreColor(call.evaluation?.lead_score)
                      )}
                    >
                      {call.evaluation?.lead_score ?? '-'}
                    </span>
                    <span className="text-xs text-gray-500">
                      ({getLeadScoreLabel(call.evaluation?.lead_score)})
                    </span>
                  </div>
                </td>

                {/* Phase */}
                <td>
                  {call.evaluation?.phase ? (
                    <span className="badge badge-blue">
                      {getPhaseLabel(call.evaluation.phase)}
                    </span>
                  ) : (
                    <span className="text-sm text-gray-400">-</span>
                  )}
                </td>

                {/* Action */}
                <td>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Load more */}
      {hasMore && (
        <div className="px-6 py-4 border-t border-gray-200">
          <button
            onClick={onLoadMore}
            disabled={loading}
            className="btn-secondary w-full"
          >
            {loading ? 'Laden...' : 'Mehr laden'}
          </button>
        </div>
      )}
    </div>
  );
}
